import { Controller } from "@hotwired/stimulus"

// Formats a phone field as it's typed: digits only, dashed into 206-910-5829.
//
// Cosmetic only. The field works with this controller entirely absent -- what
// gets stored is decided by PhoneNumber's `normalizes`, which strips the same
// punctuation server-side, and what's rejected is decided by its US_NUMBER
// validation. This just means the shape a person sees while typing matches the
// shape they see once it's saved, and that a stray letter never survives long
// enough to come back as a validation error.
export default class extends Controller {
  connect() {
    // Anything already in the field -- the value handed back after a refused
    // save, or a browser autofill -- gets the same treatment as typed input.
    this.reformat()
  }

  // The `input` event fires after the browser has applied the edit, so
  // this.element.value is the new text and selectionEnd the new caret.
  reformat() {
    const field = this.element
    const caret = field.selectionEnd
    const formatted = this.constructor.format(field.value)

    if (formatted === field.value) return

    // Count digits rather than characters: inserting or dropping a dash shifts
    // every character index after it, so a raw caret index would drift and the
    // cursor would jump on every mid-string edit.
    const digitsBeforeCaret = countDigits(field.value.slice(0, caret))

    field.value = formatted
    field.setSelectionRange(
      caretAfterDigits(formatted, digitsBeforeCaret),
      caretAfterDigits(formatted, digitsBeforeCaret)
    )
  }

  // Mirrors PhoneNumber.digits + PhoneNumber#formatted: drop punctuation, drop
  // the long-distance 1 once the number is long enough for it to be one, and
  // group 3-3-4.
  //
  // No trailing dash -- "206-" would be re-added the instant backspace removed
  // it, leaving the cursor stuck. Dashes only ever appear between groups that
  // have digits on both sides.
  static format(value) {
    let digits = value.replace(/\D/g, "")
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
    digits = digits.slice(0, 10)

    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)]
      .filter((group) => group.length > 0)
      .join("-")
  }
}

function countDigits(value) {
  return value.replace(/\D/g, "").length
}

// Where the caret belongs once `count` digits have gone by -- past any dash
// that immediately follows them, so typing the third digit leaves the cursor
// after the dash rather than before it.
function caretAfterDigits(value, count) {
  if (count === 0) return 0

  let seen = 0
  for (let i = 0; i < value.length; i++) {
    if (/\d/.test(value[i])) {
      seen++
      if (seen === count) {
        return value[i + 1] === "-" ? i + 2 : i + 1
      }
    }
  }

  return value.length
}
